/**
 * Componente unificado para gestionar álbumes de imágenes del sistema.
 * Adaptado para la web.
 */

import React, { useState, useEffect, useRef } from 'react';
import { FiUpload, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';
import { useUserImagesQuery, useUploadImageMutation, useDeleteImagesMutation } from '../../../hooks/useUserImages';
import ImageGridItem from '../common/ImageGridItem';
import ImageCarousel from '../common/ImageCarousel';
import AlbumCover from '../common/AlbumCover';
import Rodal from 'rodal';
import 'rodal/lib/rodal.css';

/**
 * Componente AlbumManager
 * 
 * Gestiona álbumes de imágenes con soporte para subir, ver y eliminar imágenes.
 * 
 * === FLUJO DE INTERACCIÓN DEL USUARIO ===
 * 
 * 1. VER IMÁGENES (CARRUSEL):
 *    - Click normal (botón izquierdo) en una imagen → Abre el carrusel de imágenes (ImageCarousel)
 *    - El carrusel permite navegar entre todas las imágenes del álbum
 * 
 * 2. ELIMINAR IMÁGENES:
 *    - Click derecho (botón derecho del mouse) en una imagen → Selecciona la imagen
 *    - Las imágenes seleccionadas se marcan con un borde rojo
 *    - Al seleccionar imágenes, aparece el botón de eliminar (icono de basura)
 *    - Click en el botón de eliminar → Confirma y elimina las imágenes seleccionadas
 *    - Solo se pueden eliminar imágenes que pertenecen al usuario actual (verificación de ownership)
 * 
 * 3. SUBIR IMÁGENES:
 *    - Click en el botón de subir (icono de upload) → Abre el selector de archivos
 *    - Seleccionar una imagen → Se sube automáticamente al álbum
 * 
 * @param {string} sectionName - Nombre de la sección para mostrar en el header
 * @param {string} album - Nombre del álbum
 * @param {number} maxLimit - Límite máximo de imágenes
 * @param {Array} selectionList - Lista de imágenes seleccionadas para eliminación
 * @param {Function} setSelectionList - Callback para actualizar la lista de selección
 * @param {Function} onDeleteComplete - Callback cuando se completa la eliminación
 * @param {Function} onEmptyStatusChange - Callback cuando cambia el estado de vacío del álbum
 * @param {Function} onImageLongPress - Callback al hacer click derecho en una imagen (para selección)
 * @param {boolean} indiscreto - Indica si mostrar en modo indiscreto (expandido)
 * @param {string} selectionModeText - Texto personalizado para el modo de selección
 * @param {Function} customOnPress - Callback personalizado al hacer clic en una imagen
 * @param {boolean} disableLongPress - Deshabilita el click derecho para selección
 * @param {boolean} disableCarousel - Deshabilita el carrusel de imágenes
 * @param {boolean} hideDeleteButton - Oculta el botón de eliminar
 * @param {boolean} hideUploadButton - Oculta el botón de subir
 * @param {boolean} isSelectable - Indica si el álbum está en modo de selección (para enviar imágenes por chat)
 * @param {Function} selectableAction - Función a ejecutar cuando se realiza la acción de selección
 * @param {string} selectableTitle - Título para el modo de selección (ej: "Enviar imagen")
 * @param {string} selectableDescription - Descripción para el modo de selección (ej: "selecciona las imagenes a enviar")
 */
export default function AlbumManager({
  sectionName,
  album,
  maxLimit = 15,
  selectionList = [],
  setSelectionList,
  onDeleteComplete,
  onEmptyStatusChange,
  onImageLongPress,
  indiscreto = false,
  selectionModeText,
  customOnPress,
  disableLongPress = false,
  disableCarousel = false,
  hideDeleteButton = false,
  hideUploadButton = false,
  isSelectable = false,
  selectableAction = null,
  selectableTitle = 'Seleccionar',
  selectableDescription = 'Selecciona imágenes',
  ...props
}) {
  const { user } = useAuth();
  // Hook para manejar notificaciones Toast
  const { showLoading, showSuccess, showError, removeToast } = useToast();
  const [modalType, setModalType] = useState(null);
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselInitialIndex, setCarouselInitialIndex] = useState(0);
  const [deletingImageIds, setDeletingImageIds] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false); // Modo de edición con animación shake
  const wasEmptyRef = useRef(true);
  const onEmptyStatusChangeRef = useRef(onEmptyStatusChange);
  const fileInputRef = useRef(null);

  useEffect(() => {
    onEmptyStatusChangeRef.current = onEmptyStatusChange;
  }, [onEmptyStatusChange]);

  useEffect(() => {
    if (modalType !== 'images' && setSelectionList) {
      setSelectionList([]);
    }
  }, [modalType, setSelectionList]);

  const { data: imagenesLista = [] } = useUserImagesQuery(album, { showError });
  const safeImagenesLista = imagenesLista || [];
  const visibleImages = safeImagenesLista.filter(foto => !deletingImageIds.has(foto.img_id));

  useEffect(() => {
    if (onEmptyStatusChangeRef.current) {
      const isEmpty = visibleImages.length === 0;
      if (wasEmptyRef.current !== isEmpty) {
        wasEmptyRef.current = isEmpty;
        onEmptyStatusChangeRef.current(album, isEmpty);
      }
    }
  }, [visibleImages.length, album]);

  const uploadMutation = useUploadImageMutation(
    album,
    {
      onSuccess: () => {
        // Callback adicional si se necesita
      },
      onError: () => {
        // Callback adicional si se necesita
      },
      showLoading,
      showSuccess,
      showError,
      removeToast
    }
  );

  const deleteMutation = useDeleteImagesMutation(album,
    {
      onSuccess: () => {
        setDeletingImageIds(new Set());
        setSelectionList([]);
        if (onDeleteComplete) onDeleteComplete();
      },
      onError: () => {
        setDeletingImageIds(new Set());
      },
      showLoading,
      showSuccess,
      showError,
      removeToast
    }
  );

  /**
   * imageArrayManager - Maneja la selección de imágenes para eliminación
   * 
   * Esta función se ejecuta cuando el usuario hace click derecho (botón derecho del mouse) en una imagen.
   * Alterna la selección de la imagen: si ya está seleccionada la deselecciona,
   * si no está seleccionada la agrega a la lista de selección.
   * 
   * Las imágenes seleccionadas se marcan visualmente con un borde rojo
   * y aparecerá el botón de eliminar en la interfaz.
   * 
   * @param {Object} foto - Objeto de imagen a seleccionar/deseleccionar
   */
  const imageArrayManager = (foto) => {
    const imgIdExists = selectionList.some(item => item.img_id === foto.img_id);
    if (imgIdExists) {
      setSelectionList(selectionList.filter(item => item.img_id !== foto.img_id));
    } else {
      setSelectionList([...selectionList, foto]);
    }
  };

  /**
   * handleDeleteImageInArray - Elimina las imágenes seleccionadas del álbum
   * 
   * Esta función se ejecuta cuando el usuario hace click en el botón de eliminar
   * (icono de basura) después de seleccionar imágenes con click derecho.
   * 
   * Flujo de eliminación:
   * 1. Verifica que el usuario esté autenticado
   * 2. Verifica que todas las imágenes seleccionadas pertenezcan al usuario actual (ownership check)
   * 3. Muestra un diálogo de confirmación
   * 4. Si el usuario confirma, marca las imágenes como "eliminándose" visualmente
   * 5. Ejecuta la mutación de eliminación en el servidor
   * 6. Limpia la lista de selección y muestra mensaje de éxito/error
   * 
   * Solo se pueden eliminar imágenes del propio álbum del usuario.
   */
  const handleDeleteImageInArray = () => {
    // Ownership check: solo permitir eliminación si el usuario está autenticado
    if (!user?.id) {
      showError('Debes estar autenticado para eliminar imágenes');
      return;
    }

    // Verificar que todas las imágenes seleccionadas pertenezcan al usuario actual
    // Esto asegura que un usuario no pueda eliminar imágenes de otros álbumes
    const imagesNotOwned = selectionList.some(img => img.user_ref_id !== user.id);
    if (imagesNotOwned) {
      showError('Solo puedes eliminar imágenes de tus propios álbumes');
      return;
    }

    // Confirmación del usuario antes de eliminar
    if (window.confirm(
      selectionList.length > 1
        ? '¿Eliminar estas fotos? Nadie más podrá volver a verlas.'
        : '¿Eliminar esta foto? Nadie más podrá volver a verla.'
    )) {
      // El toast de loading se maneja automáticamente en la mutación
      // Marcar imágenes como "eliminándose" para evitar interacciones duplicadas
      const newDeletingIds = new Set(deletingImageIds);
      selectionList.forEach(img => newDeletingIds.add(img.img_id));
      setDeletingImageIds(newDeletingIds);

      // Limpiar la lista de selección
      setSelectionList([]);

      // Ejecutar la mutación de eliminación en el servidor
      deleteMutation.mutate(selectionList);
    }
  };

  /**
   * handleDeleteSingleImage - Elimina una imagen individual del álbum
   * 
   * Esta función se ejecuta cuando el usuario hace click en el badge de eliminación
   * en modo de edición (shake mode).
   * 
   * Flujo de eliminación individual:
   * 1. Verifica que el usuario esté autenticado
   * 2. Verifica que la imagen pertenezca al usuario actual (ownership check)
   * 3. Muestra un diálogo de confirmación
   * 4. Si el usuario confirma, marca la imagen como "eliminándose" visualmente
   * 5. Ejecuta la mutación de eliminación en el servidor
   * 6. Muestra mensaje de éxito/error
   * 
   * @param {Object} image - Objeto de imagen a eliminar
   */
  const handleDeleteSingleImage = (image) => {
    // Ownership check: solo permitir eliminación si el usuario está autenticado
    if (!user?.id) {
      showError('Debes estar autenticado para eliminar imágenes');
      return;
    }

    // Verificar que la imagen pertenezca al usuario actual
    if (image.user_ref_id !== user.id) {
      showError('Solo puedes eliminar imágenes de tus propios álbumes');
      return;
    }

    // Confirmación del usuario antes de eliminar
    if (window.confirm('¿Eliminar esta foto? Nadie más podrá volver a verla.')) {
      // El toast de loading se maneja automáticamente en la mutación
      // Marcar imagen como "eliminándose" para evitar interacciones duplicadas
      const newDeletingIds = new Set(deletingImageIds);
      newDeletingIds.add(image.img_id);
      setDeletingImageIds(newDeletingIds);

      // Ejecutar la mutación de eliminación en el servidor
      deleteMutation.mutate([image]);
    }
  };

  /**
   * toggleEditMode - Alterna el modo de edición (shake mode)
   * 
   * Cuando se activa el modo de edición, las imágenes tienen una animación shake
   * y muestran badges de eliminación en la esquina superior izquierda.
   * Este es un patrón similar al de iOS cuando se edita el layout de apps.
   */
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    // Limpiar la selección de imágenes al salir del modo de edición
    if (isEditMode && setSelectionList) {
      setSelectionList([]);
    }
  };

  const handleUploadImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (user?.id) {
      // El toast de loading se maneja automáticamente en la mutación
      uploadMutation.mutate(file);
    } else {
      showError("Sesión requerida");
    }

    // Resetear el input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * handleOpenCarousel - Abre el carrusel de imágenes
   *
   * Esta función se ejecuta cuando el usuario hace click normal (botón izquierdo)
   * en una imagen. Abre el carrusel (ImageCarousel) que permite navegar
   * entre todas las imágenes del álbum en pantalla completa.
   *
   * El carrusel se abre en un modal Rodal y muestra la imagen seleccionada
   * como imagen inicial, permitiendo navegar hacia adelante y atrás.
   *
   * @param {Object} foto - Objeto de imagen a mostrar en el carrusel
   */
  const handleOpenCarousel = (foto) => {
    // Si está en modo de selección, usar el imageArrayManager para seleccionar
    if (isSelectable) {
      imageArrayManager(foto);
      return;
    }

    // Si hay un callback personalizado, usarlo en lugar del carrusel
    if (customOnPress) {
      customOnPress(foto);
      return;
    }

    // Si el modal de imágenes está abierto, cerrarlo antes de abrir el carrusel
    if (modalType === 'images') {
      setModalType(null);
    }

    // Encontrar el índice de la imagen seleccionada
    const index = visibleImages.findIndex(img => img.img_id === foto.img_id);
    if (index !== -1) {
      setCarouselInitialIndex(index);
      setShowCarousel(true);
    }
  };

  const tileSize = 100; // Tamaño base para el grid

  /**
   * Renderiza el contenido del modal para modo no indiscreto
   */
  function renderModalContent() {
    if (modalType === 'images') {
      return (
        <div className="flex flex-col h-full bg-black/80">
          <div className="flex-1 flex flex-col justify-end">
            <div className="p-4 bg-neutral-900 rounded-t-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-white text-xl font-semibold">renderModalContent() {sectionName}</h3>
                <button 
                  onClick={() => setModalType(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX size={28} />
                </button>
              </div>

              {selectionList.length ? (
                // <div className="py-4 flex justify-between items-end">
                //   <div>
                //     <p className="text-lg font-medium text-gray-300">
                //       {isSelectable ? selectableTitle : (selectionModeText || `¿Eliminar imagen${selectionList.length > 1 ? 'es' : ''} seleccionada${selectionList.length > 1 ? 's' : ''}?`)}
                //     </p>
                //     {isSelectable && selectableDescription && (
                //       <p className="text-sm text-gray-400 mt-1">{selectableDescription}</p>
                //     )}
                //   </div>
                //   {isSelectable && selectableAction ? (
                //     <button
                //       onClick={() => {
                //         console.log('=== selectableAction button clicked ===');
                //         console.log('selectionList:', selectionList);
                //         console.log('selectableAction:', selectableAction);
                //         console.log('Calling selectableAction with selectionList...');
                //         selectableAction(selectionList);
                //         console.log('selectableAction called, clearing selectionList');
                //         setSelectionList([]);
                //         console.log('=== selectableAction button click complete ===');
                //       }}
                //       className="mb-[-6px] p-2 border border-primary-500 bg-primary-500 rounded-md text-white hover:bg-primary-600 transition-colors"
                //     >
                //       <FiCheck size={24} />
                //     </button>
                //   ) : (
                //     !hideDeleteButton && (
                //       <button
                //         onClick={handleDeleteImageInArray}
                //         className="mb-[-6px] p-1 border border-neutral-700 bg-neutral-800 rounded-md text-white hover:bg-neutral-700 transition-colors"
                //       >
                //         <FiTrash2 size={24} />
                //       </button>
                //     )
                //   )}
                // </div>
                <div></div>
              ) : (
                <div className="py-4 flex justify-between items-end">
                  <p className="text-lg text-gray-300">{visibleImages.length} / {maxLimit}</p>
                  <div className="flex items-center gap-3">
                    {!uploadMutation.isPending && visibleImages.length < maxLimit && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={selectionList.length !== 0}
                        className="mb-[-6px] p-1 border border-neutral-700 bg-neutral-800 rounded-md text-white hover:bg-neutral-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiUpload size={24} />
                      </button>
                    )}
                    {uploadMutation.isPending && (
                      <div className="mb-[-6px] p-1 border border-neutral-700 bg-neutral-800 rounded-md">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <PhotoGallery
                visibleImages={visibleImages}
                selectionList={selectionList}
                tileSize={tileSize}
                imageArrayManager={imageArrayManager}
                onPress={handleOpenCarousel}
                onImageLongPress={onImageLongPress}
                disableLongPress={disableLongPress}
              />
              <div className='h-20'/>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div>
      {!indiscreto ? (
        <AlbumCover
          visibleImages={visibleImages}
          onPress={() => setModalType('images')}
        />
      ) : (
        <div>
          {/* MODO DE SELECCIÓN: Cuando hay imágenes seleccionadas para eliminar */}
          {selectionList.length ? (
            <div className="flex justify-between items-center pb-4">
              <h4 className="text-lg text-gray-300">Albúm {sectionName} {visibleImages.length} de {maxLimit} imágenes</h4>

              {/* Botón de subir imagen - solo visible si no hay selección y hay espacio */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={selectionList.length !== 0 || isEditMode}
                className="p-2 border border-neutral-700 bg-neutral-900/50 rounded-md text-white hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Subir nueva imagen"
              >
                <FiUpload size={24} />
              </button>

            </div>
          ) : (
            /* MODO NORMAL: Cuando no hay imágenes seleccionadas */
            <div className="flex justify-between items-center pb-4">
              <h4 className="text-lg text-gray-300">Albúm {sectionName} {visibleImages.length} de {maxLimit} imágenes</h4>
              <div className="flex items-center gap-3">
                {/* Botón de modo de edición (shake mode) - activa animación shake y badges de eliminación */}
                {/* Ocultar cuando isSelectable es true */}
                {!isSelectable && (
                  <button
                    onClick={toggleEditMode}
                    disabled={selectionList.length !== 0 || uploadMutation.isPending}
                    className={`p-2 border rounded-md text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isEditMode
                        ? 'border-red-500 bg-red-600 hover:bg-red-700'
                        : 'border-neutral-700 bg-neutral-900/50 hover:bg-neutral-800'
                    }`}
                    title={isEditMode ? 'Salir del modo de edición' : 'Entrar al modo de edición'}
                  >
                    {isEditMode ? 'Listo' : <FiTrash2 size={24} />}
                  </button>
                )}
                {/* Botón de subir imagen - solo visible si no hay selección y hay espacio */}
                {!hideUploadButton && !uploadMutation.isPending && visibleImages.length < maxLimit && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={selectionList.length !== 0 || isEditMode}
                    className="p-2 border border-neutral-700 bg-neutral-900/50 rounded-md text-white hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Subir nueva imagen"
                  >
                    <FiUpload size={24} />
                  </button>
                )}
                {/* Indicador de carga durante la subida */}
                {uploadMutation.isPending && (
                  <div className="mb-[-6px] p-1 border border-neutral-700 bg-neutral-900/50 rounded-md">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Input de archivo oculto para subir imágenes
              Se activa al hacer click en el botón de upload (FiUpload) */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUploadImage}
            className="hidden"
            disabled={selectionList.length !== 0 || uploadMutation.isPending}
          />

          {/* PhotoGallery: Grilla de imágenes
              - Click normal (botón izquierdo): Abre el carrusel (handleOpenCarousel)
              - Click derecho (botón derecho): Selecciona imagen para eliminación (imageArrayManager)
              - Modo edición: Muestra badges de eliminación con animación shake */}
          <PhotoGallery
            visibleImages={visibleImages}
            selectionList={selectionList}
            tileSize={tileSize}
            imageArrayManager={imageArrayManager}
            onPress={handleOpenCarousel}
            onImageLongPress={onImageLongPress}
            disableLongPress={disableLongPress}
            isEditMode={isEditMode}
            onDeleteSingleImage={handleDeleteSingleImage}
          />
        </div>
      )}

      {/* Modal para modo no indiscreto
          Se abre al hacer click en AlbumCover y muestra el álbum completo
          con las mismas funcionalidades que el modo indiscreto */}
      <Rodal
        visible={modalType !== null}
        onClose={() => setModalType(null)}
        animation="slideUp"
        closeOnEsc={true}
        closeMaskOnClick={true}
        showCloseButton={false}
        customStyles={{
          modal: {
            backgroundColor: 'transparent',
            padding: 0,
            width: '100%',
            height: '100%',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        {renderModalContent()}
      </Rodal>

      {/* ImageCarousel: Carrusel de imágenes en pantalla completa
          Se abre al hacer click normal (botón izquierdo) en una imagen (handleOpenCarousel)
          Permite navegar entre todas las imágenes del álbum */}
      {!disableCarousel && !isSelectable && (
        <ImageCarousel
          visible={showCarousel}
          onClose={() => setShowCarousel(false)}
          images={visibleImages}
          initialIndex={carouselInitialIndex}
        />
      )}
    </div>
  );
}

/**
 * Componente PhotoGallery
 * 
 * Muestra la grilla de imágenes del álbum.
 * 
 * INTERACCIÓN:
 * - Click normal (botón izquierdo) en imagen: Ejecuta onPress (abre carrusel)
 * - Click derecho (botón derecho) en imagen: Ejecuta onLongPress (selecciona para eliminación)
 * - Modo edición: Muestra badges de eliminación con animación shake
 * - Imágenes seleccionadas se muestran con borde rojo
 */
function PhotoGallery({
  visibleImages,
  selectionList,
  tileSize,
  imageArrayManager,
  onPress,
  onImageLongPress,
  disableLongPress = false,
  isEditMode = false,
  onDeleteSingleImage
}) {
  if (visibleImages.length === 0) {
    return (
      <div className="border border-neutral-700 bg-neutral-900/30 rounded-lg p-4" style={{ minHeight: tileSize * 2 }}>
        <div className="flex items-center justify-center py-8">
          <p className="text-white text-lg">No hay imágenes</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="border border-neutral-700 bg-neutral-900/30 rounded-lg p-2 grid grid-cols-3 gap-3"
      style={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      {visibleImages.map((foto, i) => {
        const isSelected = selectionList.some(sel => sel.img_id === foto.img_id);
        return (
          <ImageGridItem
            key={foto.img_id || i}
            foto={foto}
            selected={isSelected}
            onLongPress={disableLongPress ? undefined : (onImageLongPress || imageArrayManager)}
            onPress={onPress}
            isEditMode={isEditMode}
            onDeleteSingleImage={onDeleteSingleImage}
          />
        );
      })}
    </div>
  );
}
